// eslint-disable @typescript-eslint/no-explicit-any

import { useCallback, useEffect, useMemo, useRef } from 'react';
import Datasheet from '@scalartech/react-datasheet';
import { isEmpty } from 'lodash';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { buildBulkFillChanges, isFillTarget } from './fillCompatibility';
import ScalarDatasheetProps, {
  AttributesRendererResponseType,
  CellAdditions,
  CellsChanged,
  Location,
  Selection,
} from './ScalarDatasheet.props';
import useDatasheetClipboardPaste from './useDatasheetClipboardPaste';
import { cellKey, useSpreadsheetSelection } from './useSpreadsheetSelection';
import { EventType, useEventBus } from '../../context';
import { ValueViewer } from '../Spreadsheet/components';

type AttributesRenderer = (cell: ICell, row: number, col: number) => AttributesRendererResponseType;

// Module scope keeps the identity stable across renders: DataCell is a PureComponent and
// compares this prop, so a per-render fallback would re-render every cell in the grid.
const defaultValueRenderer = (cell: ICell) => cell.value;

const isLegendCell = (cell?: ICell) =>
  Boolean(cell?.isLegend || (typeof cell?.className === 'string' && cell.className.includes('legend')));

// Controls inside a cell that handle their own clicks; selection must not touch their gesture.
const SELF_HANDLED_CONTROL = [
  '[data-selection-passthrough="true"]',
  'input',
  'button',
  'select',
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="checkbox"]',
].join(',');

// In-cell elements that keep ctrl/cmd-click for themselves (the AI finding popover) but leave plain clicks to the sheet.
const SELF_HANDLED_MODIFIER_CLICK = '[data-selection-passthrough="modifier"]';

// Open dropdown menus inside a cell; their own mouse handling breaks if the sheet touches the press.
const SELF_HANDLED_MENU = [
  '[role="option"]',
  '[role="listbox"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="combobox"]',
  '.MuiPopover-root',
  '.MuiPopper-root',
  '[class*="-menu"]',
].join(',');

const defaultAttributes: AttributesRenderer = cell => ({
  ...(cell.key ? { id: `CELL-${cell.key}` } : {}),
  'data-parent': cell.parent ?? '',
  ...(cell.x !== undefined ? { 'data-row-index': String(cell.x) } : {}),
});

// True while a cell editor owns focus, so selection shortcuts never interrupt typing.
const isEditorFocused = (container: HTMLElement | null) => {
  const active = container?.ownerDocument.activeElement;
  return Boolean(
    active &&
      container?.contains(active) &&
      (active.matches('.data-editor') || ['INPUT', 'TEXTAREA'].includes(active.tagName))
  );
};

// Maps a clicked data cell to its grid position from the stamped row index, else from its CELL-<key> id.
const locateCell = (target: EventTarget | null, data: ICell[][]): Location | null => {
  const td = (target as HTMLElement | null)?.closest<HTMLElement>('td');
  if (!td || td.matches('.legend')) {
    return null;
  }
  const j = Array.prototype.indexOf.call(td.parentElement?.children ?? [], td);
  const key = td.id.startsWith('CELL-') ? td.id.slice(5) : '';
  const i = Number(td.dataset.rowIndex ?? NaN);
  if (data[i]?.[j] && (!key || data[i][j].key === key)) {
    return { i, j };
  }
  for (let row = 0; key && row < data.length; row += 1) {
    const col = data[row].findIndex(cell => cell?.key === key);
    if (col >= 0) {
      return { i: row, j: col };
    }
  }
  return null;
};

const ScalarDatasheet = ({
  valueViewer = ValueViewer,
  valueRenderer,
  selected: controlledSelected,
  ...props
}: ScalarDatasheetProps) => {
  const { clearSelectedFlag, setClearSelected, setSelectedCell, selectedCell, isColumnDragging } =
    useFeatureSpreadsheet();
  const {
    data,
    virtualization,
    onCellsChanged,
    getCellIdentity,
    isFillCompatible,
    onBulkFill,
    highlightSelectedRows,
    highlightSelectedColumns,
    onSelectionCommit,
    onSelectionSync,
  } = props;
  const pageAttributes: AttributesRenderer | undefined = props.attributesRenderer;
  const renderValue = valueRenderer || defaultValueRenderer;
  const { publish } = useEventBus();
  const containerRef = useRef<HTMLDivElement | null>(null);
  // True from a plain or shift mousedown until the document mouseup that commits the gesture.
  const mouseDownRef = useRef(false);
  // The cell a plain press landed on, held until the fork handles that same press.
  const pressedCellRef = useRef<Location | null>(null);

  const { rect, rectIntact, extras, snapshot, hasMultiSelection, setRect, ctrlToggle, clearExtras, clearAll, commit } =
    useSpreadsheetSelection({ data, getCellIdentity, onCommit: onSelectionCommit, onSync: onSelectionSync });

  const liveRect = rectIntact ? rect : null;
  const datasheetSelected = controlledSelected === undefined ? liveRect : controlledSelected;
  const { handleOnPaste } = useDatasheetClipboardPaste({
    data,
    onCellsChanged,
    selected: datasheetSelected,
    containerRef,
    enableCopyFeedback: props.enableCopyFeedback,
  });

  useEffect(() => {
    if (clearSelectedFlag) {
      clearAll();
      setClearSelected(false);
    }
  }, [clearSelectedFlag, setClearSelected, clearAll]);

  useEffect(() => {
    onSelectionSync?.(snapshot);
  }, [snapshot, onSelectionSync]);

  // Lands a plain press on its cell and drops the ctrl-picked extras. It runs where the fork handles the
  // press, so the browser renders the press once; state set in the capture phase would render on its own first.
  const settlePress = useCallback(() => {
    const at = pressedCellRef.current;
    if (at) {
      pressedCellRef.current = null;
      setRect({ start: at, end: at });
      clearExtras();
    }
  }, [setRect, clearExtras]);

  // A mouse gesture that started in this sheet commits once, on the document mouseup that ends it.
  useEffect(() => {
    const ownerDocument = containerRef.current?.ownerDocument ?? document;
    const endMouseGesture = () => {
      if (mouseDownRef.current) {
        mouseDownRef.current = false;
        // Covers a press that something inside the cell kept from the fork.
        settlePress();
        commit('replace');
      }
    };
    ownerDocument.addEventListener('mouseup', endMouseGesture);
    return () => ownerDocument.removeEventListener('mouseup', endMouseGesture);
  }, [commit, settlePress]);

  const extraKeys = useMemo(() => new Set(Array.from(extras.values(), ({ i, j }) => cellKey(i, j))), [extras]);
  const highlightedRows = useMemo(
    () => new Set(highlightSelectedRows ? snapshot?.rows : []),
    [highlightSelectedRows, snapshot]
  );
  const highlightedCols = useMemo(
    () => new Set(highlightSelectedColumns ? snapshot?.cols : []),
    [highlightSelectedColumns, snapshot]
  );

  const attributesRenderer = useCallback<AttributesRenderer>(
    (cell, row, col) => ({
      ...(pageAttributes ?? defaultAttributes)(cell, row, col),
      ...(extraKeys.has(cellKey(row, col)) ? { 'data-multi-selected': 'true' } : {}),
      ...(highlightedRows.has(row) ? { 'data-row-selected': 'true' } : {}),
      ...(highlightedCols.has(col) ? { 'data-column-selected': 'true' } : {}),
    }),
    [pageAttributes, extraKeys, highlightedRows, highlightedCols]
  );

  const handleOnSelect = useCallback(
    (selection: Selection | null) => {
      if (isColumnDragging) {
        return;
      }
      // The fork forgets its anchor in controlled mode, so a shift-click extends from the live rectangle.
      const start = selection && isEmpty(selection.start) ? (rect?.start ?? selection.end) : selection?.start;
      const end = selection?.end;
      const startCell = start ? data?.[start.i]?.[start.j] : undefined;
      const endCell = end ? data?.[end.i]?.[end.j] : undefined;
      const hitsLegend = props.disableLegendSelection && (isLegendCell(startCell) || isLegendCell(endCell));
      const next = start && end && !hitsLegend ? { start, end } : null;

      if (next) {
        const isTotalColumn = data[1]?.some(({ isTotal }) => isTotal);
        if (setSelectedCell && !isTotalColumn && startCell && (!startCell.insideLedger || startCell.allowInsideLedger)) {
          // The end cell lets RowIndicator size the indicator for multi-row selections.
          const selectedCellData = {
            ...next,
            rowNumber: next.start.i,
            cellInfo: startCell,
            ...(endCell ? { endCellInfo: endCell } : {}),
          };
          publish(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, [selectedCellData, selectedCell]);
          setSelectedCell(selectedCellData);
        }
      }
      settlePress();
      setRect(next);
      // Keyboard-driven selection has no mouseup, so it commits right away.
      if (!mouseDownRef.current) {
        commit('replace');
      }
    },
    [
      data,
      rect,
      props.disableLegendSelection,
      setSelectedCell,
      publish,
      selectedCell,
      isColumnDragging,
      settlePress,
      setRect,
      commit,
    ]
  );

  // XLSX previews: a legend click selects its row or column through a regular content cell.
  const handleLegendMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, legendCell: Element) => {
      const canSelectColumnLegend = props.allowColumnLegendSelection && legendCell.matches('.col-legend');
      const canSelectRowLegend = props.allowRowLegendSelection && legendCell.matches('.row-legend');
      if (!canSelectColumnLegend && !canSelectRowLegend) {
        setRect(null);
        return;
      }
      const rowIndex = legendCell.getAttribute('data-row-index');
      const columnLegend = legendCell.getAttribute('data-column-legend');
      const spreadsheet = containerRef.current?.closest<HTMLElement>('.spreadsheet-container');
      const contentColumns = spreadsheet?.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
      const ownerWindow = legendCell.ownerDocument.defaultView ?? window;
      let referenceCell: HTMLElement | null | undefined;
      if (canSelectRowLegend && rowIndex !== null) {
        const escapedRowIndex = ownerWindow.CSS.escape(rowIndex);
        referenceCell = contentColumns?.querySelector<HTMLElement>(
          `td[data-row-index="${escapedRowIndex}"][data-column-legend="\u03B1"]`
        ) ?? contentColumns?.querySelector<HTMLElement>(`td[data-row-index="${escapedRowIndex}"]:not(.legend)`);
      } else if (canSelectColumnLegend && columnLegend) {
        referenceCell = contentColumns?.querySelector<HTMLElement>(
          `td[data-column-legend="${ownerWindow.CSS.escape(columnLegend)}"]:not(.col-legend)`
        );
      }
      if (!referenceCell) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // Reuse a regular XLSX cell's selection path so row and column indicators stay consistent.
      referenceCell.dispatchEvent(
        new ownerWindow.MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          clientX: event.clientX,
          clientY: event.clientY,
          shiftKey: event.shiftKey,
        })
      );
    },
    [props.allowColumnLegendSelection, props.allowRowLegendSelection, setRect]
  );

  const handleMouseDownCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      const gridCell = target?.closest<HTMLElement>('table.data-grid td, table.data-grid th');
      if (!gridCell || !containerRef.current?.contains(gridCell)) {
        // The press landed outside this sheet's cells (portaled menu, blank space, scrollbar), so there is no gesture.
        return;
      }
      if (target?.closest(SELF_HANDLED_MENU)) {
        // An open dropdown reverts its edit if its press is swallowed, so the sheet leaves it entirely alone.
        return;
      }
      if (
        gridCell.matches('.editing') ||
        gridCell.querySelector('.data-editor') ||
        target?.closest(SELF_HANDLED_CONTROL)
      ) {
        // The editor or control owns this press: the sheet must not re-select and unmount it before the click lands.
        event.stopPropagation();
        return;
      }
      if (gridCell.matches('.legend') && props.disableLegendSelection) {
        handleLegendMouseDown(event, gridCell);
        return;
      }
      const modifierPress = event.ctrlKey || event.metaKey;
      if (modifierPress && (event.shiftKey || target?.closest(SELF_HANDLED_MODIFIER_CLICK))) {
        // Citation picking and claimed ctrl/cmd-clicks belong to the page, so the sheet neither selects nor tracks them.
        return;
      }
      const at = locateCell(target, data);
      const cell = at ? data[at.i][at.j] : undefined;
      if (modifierPress) {
        // The fork never sees a ctrl/cmd click: the hook toggles a data cell and commits at once.
        event.preventDefault();
        event.stopPropagation();
        if (at && cell && !isLegendCell(cell)) {
          if (!ctrlToggle(at.i, at.j)) {
            // Nothing is left selected, so the row and column indicators must not act on the old active cell.
            setSelectedCell?.(undefined);
          }
          containerRef.current?.querySelector<HTMLElement>('span.data-grid-container')?.focus({ preventScroll: true });
        }
        return;
      }
      if (!at || !cell || cell.disableEvents) {
        // Nothing the fork will select (blank area, legend, disabled cell), so there is no gesture to track.
        return;
      }
      mouseDownRef.current = true;
      // A plain press collapses onto its cell, where the fork is about to put its rectangle, so nothing stale
      // paints mid-drag; a shift press extends the rectangle instead.
      pressedCellRef.current = !event.shiftKey && !isColumnDragging ? at : null;
    },
    [
      data,
      props.disableLegendSelection,
      handleLegendMouseDown,
      isColumnDragging,
      ctrlToggle,
      setSelectedCell,
    ]
  );

  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isEditorFocused(containerRef.current)) {
        return;
      }
      if (event.key === 'Escape') {
        // Escape drops a multi-cell selection whole, active cell included; a single cell stays put.
        if (hasMultiSelection) {
          clearAll();
          setSelectedCell?.(undefined);
        }
        return;
      }
      if (!extras.size || (event.key !== 'Delete' && event.key !== 'Backspace')) {
        return;
      }
      // The fork only clears its rectangle, so every selected cell is cleared here in one batch.
      event.preventDefault();
      event.stopPropagation();
      const changes: CellsChanged[] = (snapshot?.cells ?? [])
        .filter(({ cell }) => isFillTarget(cell))
        .map(({ cell, row, col }) => ({ cell, row, col, value: '' }));
      if (changes.length) {
        onCellsChanged?.(changes);
      }
    },
    [hasMultiSelection, extras.size, snapshot, clearAll, setSelectedCell, onCellsChanged]
  );

  // In grids that report bulk fills, one edit inside a multi-cell selection fans out to every compatible
  // selected cell; anything else passes through.
  const handleCellsChanged = useCallback(
    (changes: CellsChanged[], additions?: CellAdditions) => {
      const plan =
        onBulkFill && changes.length === 1 && snapshot && snapshot.cells.length > 1
          ? buildBulkFillChanges(changes[0], snapshot, isFillCompatible)
          : null;
      if (!plan || (plan.filled === 1 && plan.skipped === 0)) {
        onCellsChanged?.(changes, additions);
        return;
      }
      onCellsChanged?.(plan.changes, additions);
      onBulkFill?.({ filled: plan.filled, skipped: plan.skipped });
    },
    [snapshot, isFillCompatible, onCellsChanged, onBulkFill]
  );

  return (
    <div ref={containerRef} onMouseDownCapture={handleMouseDownCapture} onKeyDownCapture={handleKeyDownCapture}>
      <Datasheet
        parsePaste={handleOnPaste}
        onSelect={handleOnSelect}
        selected={datasheetSelected}
        // any is used because the library does not have typing correctly configured
        // when migrating from js->ts the props incoming to this component are typed
        // but the library has its own typing it expects
        valueRenderer={renderValue as any}
        virtualization={virtualization}
        valueViewer={valueViewer as any}
        attributesRenderer={attributesRenderer as any}
        cellRenderer={props.cellRenderer as any}
        dataEditor={props.dataEditor as any}
        onCellsChanged={(onCellsChanged ? handleCellsChanged : undefined) as any}
        rowRenderer={props.rowRenderer as any}
        data={props.data as any}
        className={props.className as any}
        dataRenderer={props.dataRenderer as any}
        isCellNavigable={props.isCellNavigable as any}
      />
    </div>
  );
};

export default ScalarDatasheet;
